/**
 * DashboardPage v4 — Cockpit de fundador, marca Neocoop.
 *
 * Dirección (post-inspiración Mercury/Stripe para fundadores):
 *   - Liderar con UN número (patrimonio) en un hero oscuro premium.
 *   - Densidad baja, resultados a la vista, una acción clara.
 *   - Marca real: isotipo Neocoop, naranja #FF5B04, DM Sans, composición
 *     monocromática en tintes de marca. Movimiento sobrio (count-up + draw),
 *     sin glow genérico.
 *
 * Datos: dashboard_resumen (0056) + dashboard_creditos_resumen (0199).
 * Realtime + poll 90s.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useRiesgoCartera } from '@/components/RiesgoCartera'
import { useAuth } from '@/lib/auth'
import { formatQ } from '@/lib/format'
import { BRAND, IS_DEMO, TENANT } from '@/lib/brand'

const TZ = 'America/Guatemala'
const O = '#FF5B04'
const O2 = '#ff8a47'
const O3 = '#ffc7a3'
const INK = 'var(--n-900)'
const MUT = 'var(--n-500)'
const HAIR = '#ECEEF1'
const POS = '#0F8A57'
const NEG = '#C0392B'
const DISP = "var(--font-display, 'DM Sans', -apple-system, sans-serif)"

type SeriePoint = { fecha: string; saldo: number; neto_dia: number }
type Resumen = {
  patrimonio: { total: number; aportaciones: number; ahorro: number; plazo_fijo: number }
  patrimonio_serie_30d: SeriePoint[]
  patrimonio_delta_7d: { monto_anterior: number; delta: number; pct: number | null }
  asociados: { activos: number; prospectos: number; nuevos_semana: number; total: number }
  plazos_fijos: { activos: number; total_invertido: number }
  cumplimiento: { kyc_review: number; reportes_pendientes: number; feis_incompleto: number }
  caja_estado: { caja_codigo: string } | null
  proximos_vencimientos_pf: Array<{
    certificado_no: string | null; capital: number; tasa: number
    fecha_vencimiento: string; asociado_nombre: string; dias_restantes: number
  }>
}
type Creditos = {
  desembolsado_total: number; desembolsado_count: number
  activos_count: number; cartera_saldo: number
  recuperado_capital: number; intereses_cobrados: number
  en_mora_count: number; mora_monto: number
  desembolsado_semana: number; desembolsado_semana_count: number
}

// ── count-up ────────────────────────────────────────────────────────────────
function useCountUp(target: number, dur = 1200) {
  const [v, setV] = useState(0)
  const ref = useRef(target)
  useEffect(() => {
    const from = ref.current === target ? 0 : ref.current
    ref.current = target
    let raf = 0, t0 = 0
    const step = (ts: number) => {
      if (!t0) t0 = ts
      const p = Math.min((ts - t0) / dur, 1)
      const e = 1 - Math.pow(1 - p, 3)
      setV(from + (target - from) * e)
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, dur])
  return v
}
function Money({ value, decimals = 2, style }: { value: number; decimals?: number; style?: React.CSSProperties }) {
  const v = useCountUp(value)
  return <span style={{ fontVariantNumeric: 'tabular-nums', ...style }}>
    Q {v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
  </span>
}
function Count({ value, style }: { value: number; style?: React.CSSProperties }) {
  const v = useCountUp(value)
  return <span style={{ fontVariantNumeric: 'tabular-nums', ...style }}>{Math.round(v).toLocaleString('en-US')}</span>
}

export function DashboardPage() {
  const { session } = useAuth()
  const [data, setData] = useState<Resumen | null>(null)
  const [cred, setCred] = useState<Creditos | null>(null)
  const riesgo = useRiesgoCartera()  // mora = 31+ días calendario (0392)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [now, setNow] = useState<Date>(new Date())

  const firstName = useMemo(() => {
    const meta = session?.user?.user_metadata as Record<string, unknown> | undefined
    const fullName = (meta?.full_name as string | undefined) ?? (meta?.name as string | undefined)
    if (fullName && fullName.trim()) {
      const f = fullName.trim().split(/\s+/)[0] ?? ''
      return f.charAt(0).toUpperCase() + f.slice(1).toLowerCase()
    }
    const handle = (session?.user?.email ?? 'admin').split('@')[0] ?? 'admin'
    const token = /[._-]/.test(handle) ? handle.split(/[._-]/)[0] : handle.slice(0, 4)
    return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
  }, [session])

  const greeting = useMemo(() => {
    const h = Number(now.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: TZ }))
    return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches'
  }, [now])

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        supabase.rpc('dashboard_resumen'),
        supabase.rpc('dashboard_creditos_resumen'),
      ])
      if (r1.error) setError(r1.error.message)
      else { setData(r1.data as Resumen); setError(null) }
      if (!r2.error && r2.data && !(r2.data as { error?: string }).error) setCred(r2.data as Creditos)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos cargar el dashboard.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    load()
    const refresh = setInterval(load, 90_000)
    const tick = setInterval(() => setNow(new Date()), 30_000)
    return () => { clearInterval(refresh); clearInterval(tick) }
  }, [load])

  useEffect(() => {
    const ch = supabase.channel('dashboard-v4')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'movimiento_caja' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'asociado' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'plazo_fijo_detalle' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'credito' }, () => load())
      .subscribe()
    return () => { void supabase.removeChannel(ch) }
  }, [load])

  if (loading && !data) return <div style={{ padding: 60, textAlign: 'center', color: MUT }}>Cargando…</div>
  if (error || !data) return <div className="error-banner">{error ?? 'No pudimos cargar el dashboard.'}</div>

  const p = data.patrimonio
  const d7 = data.patrimonio_delta_7d
  const up = (d7?.delta ?? 0) >= 0
  const fecha = now.toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long', timeZone: TZ })
  const pct = (n: number) => (p.total ? Math.round((n / p.total) * 100) : 0)
  const prox = data.proximos_vencimientos_pf?.[0]
  const alertas = data.cumplimiento.kyc_review + data.cumplimiento.reportes_pendientes + data.cumplimiento.feis_incompleto

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {IS_DEMO && (
        <div className="demo-brand-banner">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <img src={BRAND.logoSobreColor} alt={BRAND.nombre} className="demo-brand-banner-logo" />
            <div className="demo-brand-banner-txt">{TENANT?.razon_social ?? BRAND.nombre}</div>
          </div>
          <span className="demo-brand-banner-chip">NeoCore · Demo</span>
          <img src={BRAND.isoOscuro} alt="" aria-hidden="true" className="demo-brand-banner-iso" />
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontFamily: DISP, fontSize: 22, fontWeight: 700, color: INK, letterSpacing: -0.4 }}>
            {greeting}, {firstName}.
          </div>
          <div style={{ fontSize: 12, color: MUT, marginTop: 2, textTransform: 'capitalize' }}>{fecha}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {data.caja_estado ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: MUT }}>
              <span style={{ width: 7, height: 7, borderRadius: 4, background: POS, display: 'inline-block' }} />
              {data.caja_estado.caja_codigo}
            </span>
          ) : null}
          {alertas > 0 ? (
            <Link to="/neocore/asociados?filtro=feis_incompleto"
              style={{ fontSize: 12, color: '#B45309', textDecoration: 'none', fontWeight: 600 }}>
              {alertas} por atender
            </Link>
          ) : null}
        </div>
      </div>

      {/* HERO */}
      <div style={{
        position: 'relative', borderRadius: 20, overflow: 'hidden', padding: '26px 30px 4px', color: '#fff',
        background: 'linear-gradient(160deg,#17120f 0%,#0c0a09 60%,#0a0908 100%)',
      }}>
        <Isotipo />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, position: 'relative', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.4, textTransform: 'uppercase', color: '#8a8079' }}>
                Patrimonio bajo administración
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#c9c2bb', fontWeight: 600, letterSpacing: 0.8 }}>
                <span className="neo-pulse" style={{ width: 5, height: 5, borderRadius: 3, background: O, display: 'inline-block' }} />
                EN VIVO
              </span>
            </div>
            <Money value={p.total} style={{ display: 'block', fontFamily: DISP, fontSize: 58, fontWeight: 700, letterSpacing: -2.3, lineHeight: 1, marginTop: 10 }} />
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 12, fontSize: 12.5 }}>
              <span style={{ background: 'rgba(255,91,4,.16)', color: O2, fontWeight: 700, padding: '4px 10px', borderRadius: 8 }}>
                {up ? '+' : ''}{formatQ(d7?.delta ?? 0)} · {(d7?.pct ?? 0).toFixed(1)}%
              </span>
              <span style={{ color: '#8a8079', fontWeight: 500 }}>vs. hace 7 días</span>
            </div>
          </div>
          {/* composición mono */}
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: '#8a8079', marginBottom: 10 }}>Composición</div>
            <div style={{ display: 'flex', height: 8, borderRadius: 5, overflow: 'hidden', gap: 2 }}>
              <div style={{ width: `${pct(p.plazo_fijo)}%`, background: O }} />
              <div style={{ width: `${pct(p.ahorro)}%`, background: O2 }} />
              <div style={{ width: `${pct(p.aportaciones)}%`, background: O3, minWidth: 3 }} />
            </div>
            <div style={{ marginTop: 12, fontSize: 12, lineHeight: 2 }}>
              <CompRow color={O} label="Plazo fijo" value={p.plazo_fijo} pct={pct(p.plazo_fijo)} strong />
              <CompRow color={O2} label="Ahorro" value={p.ahorro} pct={pct(p.ahorro)} />
              <CompRow color={O3} label="Aportaciones" value={p.aportaciones} pct={pct(p.aportaciones)} />
            </div>
          </div>
        </div>
        <AreaChart serie={data.patrimonio_serie_30d} />
      </div>

      {/* RESULTADO · crédito */}
      <Grid>
        <Tile label="Créditos desembolsados" to="/neocore/creditos" node={<Money value={cred?.desembolsado_total ?? 0} decimals={0} />}
          sub={`${cred?.desembolsado_count ?? 0} colocados · +${cred?.desembolsado_semana_count ?? 0} sem`} tone="pos" />
        <Tile label="Cartera activa" to="/neocore/creditos" node={<Money value={cred?.cartera_saldo ?? 0} decimals={0} />}
          sub={`${cred?.activos_count ?? 0} vigentes`} />
        <Tile label="Recuperado" node={<Money value={(cred?.recuperado_capital ?? 0) + (cred?.intereses_cobrados ?? 0)} decimals={0} />}
          sub={`capital + Q${(cred?.intereses_cobrados ?? 0).toFixed(0)} interés`} tone="pos" />
        <Tile label="Mora 31+ días" to="/neocore/carteras" node={<Money value={riesgo.mora?.saldo ?? 0} decimals={0} />}
          sub={riesgo.cargado
            ? `${(riesgo.mora?.pct ?? 0).toFixed(1)}% · ${riesgo.mora?.creditos ?? 0} créditos · pre-mora ${(riesgo.get('pre_mora')?.pct ?? 0).toFixed(1)}%`
            : '—'}
          tone={(riesgo.mora?.saldo ?? 0) > 0 ? 'neg' : 'pos'} />
      </Grid>

      {/* CAPTACIÓN */}
      <Grid>
        <Tile label="Asociados activos" to="/neocore/asociados" node={<Count value={data.asociados.activos} />}
          sub={`+${data.asociados.nuevos_semana} esta semana`} tone="pos" />
        <Tile label="Plazo fijo" to="/neocore/plazos-fijos" node={<Money value={data.plazos_fijos.total_invertido} decimals={0} />}
          sub={`${data.plazos_fijos.activos} certificados`} />
        <Tile label="Ahorro corriente" to="/neocore/ahorros" node={<Money value={p.ahorro} decimals={0} />} sub="captación a la vista" />
        <Tile label="Aportaciones" node={<Money value={p.aportaciones} decimals={0} />} sub="capital social" />
      </Grid>

      {/* ACCIÓN */}
      <Link to="/neocore/plazos-fijos" style={{ textDecoration: 'none' }}>
        <div style={{ border: `1px solid ${HAIR}`, borderRadius: 14, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fffaf3' }}>
          <div style={{ fontSize: 13, color: INK }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: MUT, marginRight: 12 }}>Próximo vencimiento</span>
            {prox ? (<><b>{formatQ(prox.capital)}</b> · {prox.asociado_nombre}
              <span style={{ color: MUT }}> · {prox.certificado_no} · {(prox.tasa * 100).toFixed(0)}% · en {prox.dias_restantes} días</span></>)
              : <span style={{ color: MUT }}>Sin vencimientos próximos.</span>}
          </div>
          <span style={{ fontSize: 12, color: O, fontWeight: 700 }}>Ver plazos fijos →</span>
        </div>
      </Link>

      <style>{`
        @keyframes neoPulse { 0%,100%{opacity:1} 50%{opacity:.3} }
        .neo-pulse { animation: neoPulse 1.8s ease-in-out infinite; }
        .neo-tile { transition: background .15s; }
        .neo-tile:hover { background: #FAFBFC !important; }
      `}</style>
    </div>
  )
}

// ── helpers ──────────────────────────────────────────────────────────────────
function Isotipo() {
  return (
    <svg viewBox="0 0 120 120" aria-hidden="true"
      style={{ position: 'absolute', right: -30, top: -24, width: 260, height: 260, opacity: 0.06 }}>
      <path fill="#fff" d="M86.48,77.31c-3.18.43-6.04.06-9.04.04l-11.23-9.54c-5.92-5.03-24.76-17.02-32.37-12.69-3.06,1.74-4.16,4.97-3.12,8.25l4.46,14.1c-9.35-.57-16.12-8.2-16.37-17.02-.25-8.89,6.28-16.88,15.33-17.88,2.9-.32,5.73-.06,8.63-.03l8.07,7.23c7.82,7,18.84,14.83,28.81,18.19,3.3,1.11,6.68.07,8.59-2.85,1.6-2.23,2.07-4.93,1.28-7.61l-4.47-15.07c9.05.61,15.94,8,16.32,16.75.37,8.81-5.79,16.9-14.89,18.13Z" />
      <path fill="#fff" d="M77.49,84.86c-.67,10.44-10.33,17.71-20.76,15.73-8.31-1.58-14.14-9.03-14.06-17.59l.18-19.3c1.52,7.48,3.84,14.5,8.53,20.44,2.88,3.61,6.94,5.55,11.49,4.98l14.61-4.25Z" />
      <path fill="#fff" d="M77.54,56.46c-1.66-7.54-4.02-16.7-9.89-22.26-3.17-3-7.43-4.19-11.65-2.94l-13.32,3.94c.64-8.18,6.78-14.91,14.95-16-9.72-1.29,19.13,5.39,19.85,15.42.52,7.31.08,14.39.05,21.84Z" />
    </svg>
  )
}

function CompRow({ color, label, value, pct, strong }: { color: string; label: string; value: number; pct: number; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', color: strong ? '#fff' : '#c9c2bb' }}>
      <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 2, background: color, marginRight: 7 }} />{label}</span>
      <span style={{ color: '#fff', fontWeight: 600 }}>{fmtK(value)} · {pct}%</span>
    </div>
  )
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: HAIR, border: `1px solid ${HAIR}`, borderRadius: 16, overflow: 'hidden' }}>
      {children}
    </div>
  )
}

function Tile({ label, node, sub, tone, to }: {
  label: string; node: React.ReactNode; sub?: string; tone?: 'pos' | 'neg'; to?: string
}) {
  const sc = tone === 'pos' ? POS : tone === 'neg' ? NEG : MUT
  const inner = (
    <div className="neo-tile" style={{ background: '#fff', padding: '16px 18px', height: '100%', cursor: to ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: MUT }}>{label}</div>
      <div style={{ fontFamily: DISP, fontSize: 26, fontWeight: 700, color: INK, letterSpacing: -0.9, marginTop: 6 }}>{node}</div>
      {sub ? <div style={{ fontSize: 11, marginTop: 4, color: sc, fontWeight: tone ? 600 : 500 }}>{sub}</div> : null}
    </div>
  )
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{inner}</Link> : inner
}

function AreaChart({ serie }: { serie: SeriePoint[] }) {
  const ref = useRef<SVGPathElement | null>(null)
  const W = 1000, H = 96
  const geo = useMemo(() => {
    if (!serie || serie.length < 2) return null
    const vals = serie.map((s) => s.saldo)
    const mn = Math.min(...vals), mx = Math.max(...vals), sp = mx - mn || 1
    const st = W / (serie.length - 1)
    const y = (v: number) => H - ((v - mn) / sp) * (H - 20) - 10
    const line = serie.map((s, i) => `${i ? 'L' : 'M'} ${(i * st).toFixed(1)} ${y(s.saldo).toFixed(1)}`).join(' ')
    return { line, area: `${line} L ${W} ${H} L 0 ${H} Z`, lx: (serie.length - 1) * st, ly: y(serie[serie.length - 1].saldo) }
  }, [serie])

  useEffect(() => {
    const el = ref.current
    if (!el || !geo) return
    const L = el.getTotalLength()
    el.style.strokeDasharray = String(L)
    el.style.strokeDashoffset = String(L)
    const anim = el.animate([{ strokeDashoffset: L }, { strokeDashoffset: 0 }],
      { duration: 1500, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' })
    return () => anim.cancel()
  }, [geo])

  if (!geo) return <div style={{ height: 92 }} />
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 92, display: 'block', marginTop: 2 }}>
      <defs>
        <linearGradient id="neo-pat" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={O} stopOpacity="0.22" />
          <stop offset="100%" stopColor={O} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={geo.area} fill="url(#neo-pat)" />
      <path ref={ref} d={geo.line} fill="none" stroke={O} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      <circle cx={geo.lx} cy={geo.ly} r="3" fill="#fff" stroke={O} strokeWidth="2" />
    </svg>
  )
}

function fmtK(n: number): string {
  const a = Math.abs(n)
  if (a >= 1_000_000) return `Q ${(n / 1_000_000).toFixed(2)}M`
  if (a >= 1000) return `Q ${(n / 1000).toFixed(1)}k`
  return formatQ(n)
}
